import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import AIModelService from "../../../service/admin/aiModelService";
import WebButton from "../../../component/library/webButton";
import "./style.css";

export default function PromptForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();
  const isEdit = id && id !== "new";

  const [models, setModels] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    page: "",
    action: "",
    systemPrompt: "You are a helpful AI assistant specializing in e-commerce product content.",
    userPromptTemplate: "",
    assignedModel: "",
    maxTokens: "",
    temperature: "",
    responseFormat: "text",
    isActive: true,
    description: "",
  });

  useEffect(() => {
    fetchModels();
    fetchAvailablePages();
    if (isEdit) fetchPrompt();
  }, [id]);

  const fetchModels = async () => {
    try {
      const res = await AIModelService.getAllModels({ isActive: "true" });
      if (res?.status?.code === 200) {
        setModels(res.data?.models || []);
      }
    } catch (err) {
      console.error("Failed to fetch models", err);
    }
  };

  const fetchAvailablePages = async () => {
    try {
      const res = await AIModelService.getAvailablePages();
      if (res?.status?.code === 200) {
        setAvailablePages(res.data?.pages || []);
      }
    } catch (err) {
      console.error("Failed to fetch pages", err);
    }
  };

  const fetchPrompt = async () => {
    loader.showLoading(true);
    try {
      const res = await AIModelService.getPromptById(id);
      if (res?.status?.code === 200 && res.data?.prompt) {
        const prompt = res.data.prompt;
        setFormData({
          ...prompt,
          assignedModel: prompt.assignedModel?._id || "",
          maxTokens: prompt.maxTokens || "",
          temperature: prompt.temperature || "",
        });
      } else {
        message.showToast("Prompt not found", ToastTypes.ERROR);
        navigate("/admin/ai-management");
      }
    } catch (err) {
      message.showToast("Failed to load prompt", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePageSelect = (pageValue) => {
    handleChange("page", pageValue);
    handleChange("action", "");
  };

  const getActionsForPage = () => {
    const pageConfig = availablePages.find((p) => p.page === formData.page);
    return pageConfig?.actions || [];
  };

  const generateSlug = () => {
    if (formData.page && formData.action) {
      const slug = `${formData.page}-${formData.action}`.toLowerCase().replace(/\s+/g, "-");
      handleChange("slug", slug);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.slug || !formData.page || !formData.action || !formData.userPromptTemplate) {
      message.showToast("Please fill all required fields", ToastTypes.WARNING);
      return;
    }

    loader.showLoading(true);
    try {
      const payload = {
        ...formData,
        assignedModel: formData.assignedModel || null,
        maxTokens: formData.maxTokens ? parseInt(formData.maxTokens) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
      };

      let res;
      if (isEdit) {
        res = await AIModelService.updatePrompt(id, payload);
      } else {
        res = await AIModelService.createPrompt(payload);
      }

      if (res?.status?.code === 200) {
        message.showToast(isEdit ? "Prompt updated" : "Prompt created", ToastTypes.SUCCESS);
        navigate("/admin/ai-management");
      } else {
        message.showToast(res?.error?.message || "Operation failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast("Operation failed", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  return (
    <div className="aim-prompt-form-container">
      <div className="aim-form-header">
        <button className="aim-back-btn" onClick={() => navigate("/admin/ai-management")}>
          ← Back
        </button>
        <h1>{isEdit ? "Edit AI Prompt" : "Add AI Prompt"}</h1>
      </div>

      <form onSubmit={handleSubmit} className="aim-prompt-form">
        <div className="aim-form-section">
          <h3>Basic Information</h3>

          <div className="aim-form-row">
            <div className="aim-form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g., Product Title Generator"
              />
            </div>

            <div className="aim-form-group">
              <label>Slug *</label>
              <div className="aim-slug-input">
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  placeholder="e.g., product-draft-edit-generate-title"
                />
                <button type="button" onClick={generateSlug} className="aim-generate-slug-btn">
                  Auto
                </button>
              </div>
              <small>Unique identifier for this prompt</small>
            </div>
          </div>

          <div className="aim-form-row">
            <div className="aim-form-group">
              <label>Page *</label>
              <select value={formData.page} onChange={(e) => handlePageSelect(e.target.value)}>
                <option value="">Select page</option>
                {availablePages.map((p) => (
                  <option key={p.page} value={p.page}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            </div>

            <div className="aim-form-group">
              <label>Action *</label>
              <select
                value={formData.action}
                onChange={(e) => handleChange("action", e.target.value)}
                disabled={!formData.page}
              >
                <option value="">Select action</option>
                {getActionsForPage().map((a) => (
                  <option key={a.action} value={a.action}>
                    {a.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="aim-form-group">
            <label>Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Brief description of what this prompt does"
            />
          </div>
        </div>

        <div className="aim-form-section">
          <h3>Prompt Configuration</h3>

          <div className="aim-form-group">
            <label>System Prompt</label>
            <textarea
              value={formData.systemPrompt}
              onChange={(e) => handleChange("systemPrompt", e.target.value)}
              placeholder="Instructions for the AI model's behavior"
              rows={3}
            />
            <small>Sets the AI's role and behavior</small>
          </div>

          <div className="aim-form-group">
            <label>User Prompt Template *</label>
            <textarea
              value={formData.userPromptTemplate}
              onChange={(e) => handleChange("userPromptTemplate", e.target.value)}
              placeholder="Use {{placeholders}} for dynamic data. E.g., Generate a title for: {{title}}"
              rows={6}
            />
            <small>
              Use {"{{placeholder}}"} syntax for dynamic data: {"{{title}}"}, {"{{description}}"}, {"{{category}}"}, {"{{brand}}"}
            </small>
          </div>

          <div className="aim-form-row">
            <div className="aim-form-group">
              <label>Response Format</label>
              <select value={formData.responseFormat} onChange={(e) => handleChange("responseFormat", e.target.value)}>
                <option value="text">Text</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>
        </div>

        <div className="aim-form-section">
          <h3>Model Assignment</h3>

          <div className="aim-form-group">
            <label>Assigned Model</label>
            <select value={formData.assignedModel} onChange={(e) => handleChange("assignedModel", e.target.value)}>
              <option value="">-- No Model (Use Default) --</option>
              {models.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.displayName} ({m.provider})
                </option>
              ))}
            </select>
            <small>Select which AI model should be used for this prompt</small>
          </div>

          <div className="aim-form-row">
            <div className="aim-form-group">
              <label>Max Tokens Override</label>
              <input
                type="number"
                value={formData.maxTokens}
                onChange={(e) => handleChange("maxTokens", e.target.value)}
                placeholder="Leave empty for model default"
              />
            </div>

            <div className="aim-form-group">
              <label>Temperature Override</label>
              <input
                type="number"
                value={formData.temperature}
                onChange={(e) => handleChange("temperature", e.target.value)}
                placeholder="Leave empty for model default"
                step={0.1}
                min={0}
                max={2}
              />
            </div>
          </div>

          <div className="aim-form-row checkboxes">
            <label className="aim-checkbox-label">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange("isActive", e.target.checked)}
              />
              Active
            </label>
          </div>
        </div>

        <div className="aim-form-actions">
          <WebButton
            type="button"
            text="Cancel"
            onClick={() => navigate("/admin/ai-management")}
            className="aim-cancel-btn"
          />
          <WebButton type="submit" text={isEdit ? "Update Prompt" : "Create Prompt"} className="aim-submit-btn" />
        </div>
      </form>
    </div>
  );
}
